'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, User, Warehouse, Code } from 'lucide-react';
import { useMediaQuery } from './useMediaQuery';

import { ServiceCard } from './ServiceCard';
import { ServiceTabs } from './ServiceTabs';
import { ServiceDemo } from './ServiceDemo';
import { BadgeWithIcon } from '@/components/ui/BadgeWithIcon/BadgeWithIcon';

import styles from './ServicesSection.module.css';

const services = [
    {
        icon: MessageCircle,
        title: 'Поддержка 24/7',
        titleMobile: 'Чат',
        description: 'Персональный менеджер для решения любых вопросов',
        demoType: 'support',
        badge: 'Онлайн',
    },
    {
        icon: User,
        title: 'Личный кабинет',
        titleMobile: 'Кабинет',
        description: 'Управляйте всеми отправлениями в одном месте',
        demoType: 'dashboard',
    },
    {
        icon: Warehouse,
        title: 'Фулфилмент для маркетплейсов',
        titleMobile: 'Фулфилмент',
        description: 'Комплексное решение для вашего бизнеса',
        demoType: 'fulfillment',
        badge: 'Популярно',
    },
    {
        icon: Code,
        title: 'API',
        titleMobile: 'Фулфилмент',
        description: 'Встройте доставку в ваш сервис',
        demoType: 'api',
        badge: 'Новое',
    },
];

export const ServicesSection = () => {
    const [activeServiceId, setActiveServiceId] = React.useState(services?.[0]?.demoType);

    const isMobile = useMediaQuery('(max-width: 768px)');

    const tabItems = services.map((service) => ({
        id: service.demoType,
        label: service.titleMobile,
        icon: service.icon,
    }));

    return (
        <section className={styles.section}>
            <div className={styles.container}>
                <div className={styles.contentWrapper}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className={styles.header}
                    >
                        <BadgeWithIcon
                            color="--color-green-rgb"
                            icon="EmojiEvents"
                            title="Наши преимущества"
                        />
                        <h2 className={styles.title}>Почему выбирают СДЭК?</h2>
                        <p className={styles.description}>
                            Мы создаем надежные логистические решения, которые помогают вашему
                            бизнесу расти и развиваться
                        </p>
                    </motion.div>

                    <div className={styles.grid}>
                        <div className={styles.servicesList}>
                            {isMobile ? (
                                <ServiceTabs
                                    tabs={tabItems}
                                    activeTabId={activeServiceId}
                                    onTabClick={setActiveServiceId}
                                />
                            ) : (
                                services.map((service) => (
                                    <motion.div
                                        key={service.demoType}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: services.indexOf(service) * 0.1 }}
                                    >
                                        <ServiceCard
                                            {...service}
                                            isActive={activeServiceId === service.demoType}
                                            onClick={() => setActiveServiceId(service.demoType)}
                                        />
                                    </motion.div>
                                ))
                            )}
                        </div>

                        <motion.div
                            className={styles.demoPanel}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                            key={activeServiceId}
                        >
                            <ServiceDemo type={activeServiceId} />
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};
